import * as React from 'react';
import b_ from 'b_';
import { compose } from 'recompose';
import translate, { InjectedTranslateProps } from 'shared/utils/translate';
import { Investment_IdeaType } from 'types/common';
import {
  Investment_BuyOrderStatus,
  Stocks_BuyOrderStatus,
} from '../../../../../../__generated__/globalTypes';
import { OrderType } from '../../../../Order/types';
import { ArchiveStockButton } from '../../ArchiveStockButton';
import { ArchiveIdeaButton } from '../../ArchiveIdeaButton';
import { OrderStatusTooltipFragment } from './__generated__/OrderStatusTooltipFragment';
import { Row } from './components/Row';
import { Comment } from './components/Comment';
import { getLaterDate } from '../utils';

import './index.scss';

type Order = OrderStatusTooltipFragment;

interface Props {
  order: Order;
}

type Enhanced = Props & InjectedTranslateProps;

const enhance = compose<Enhanced, Props>(translate);

const b = b_.with('order-card-status-tooltip');

class StatusTooltip extends React.Component<Enhanced> {
  render() {
    const { t, order } = this.props;

    const {
      isExecuting,
      auditInfo: { createdAt },
    } = order;

    if (isExecuting) {
      return (
        <div className={`rc-overlay ${b()}`}>
          <div className={b('pendiing')}>
            {t('Order is in the process of execution on the exchange')}
          </div>
        </div>
      );
    }

    return (
      <div className={`rc-overlay ${b()}`}>
        <table>
          <tbody>
            <Row title={t('Order submitted')}>{createdAt}</Row>
            {this.renderLockAt()}
            {this.renderStartAt()}
            {this.renderRejectedAt()}
          </tbody>
        </table>
        {this.renderComment()}
        {this.renderArchiveButton()}
      </div>
    );
  }

  private renderArchiveButton() {
    const { order } = this.props;

    if (order.__typename === OrderType.Stock && order.canArchived) {
      return (
        <div className={b('button-cont')}>
          <ArchiveStockButton orderId={order.id} />
        </div>
      );
    }

    if (order.__typename === OrderType.Idea && order.canArchive && order.product != null) {
      return (
        <div className={b('button-cont')}>
          <ArchiveIdeaButton orderId={order.id} ideaType={order.product!.ideaType} />
        </div>
      );
    }

    return null;
  }

  private renderStartAt() {
    const { t, order } = this.props;

    if (order.__typename === OrderType.Stock) {
      return <Row title={t('Start')}>{order.auditInfo.createdAt}</Row>;
    }

    if (order.isExecuting) {
      return null;
    }

    if (order.product?.ideaType === Investment_IdeaType.BUY_AND_HOLD) {
      const date = getLaterDate(order.lockDate, order.startDate);
      return date && <Row title={t('Expected start')}>{date}</Row>;
    }

    if (
      order.startDate &&
      [
        Investment_BuyOrderStatus.ACCEPTED,
        Investment_BuyOrderStatus.LOCKED,
        Investment_BuyOrderStatus.SEALED,
        Investment_BuyOrderStatus.POSTPONED,
        Investment_BuyOrderStatus.CREATED,
        Investment_BuyOrderStatus.PREPARED_TO_CANCEL,
      ].includes(order.status)
    ) {
      return <Row title={t('Start')}>{order.startDate}</Row>;
    }

    return null;
  }

  private renderLockAt() {
    const { t, order } = this.props;

    if (order.__typename === OrderType.Stock) {
      return null;
    }

    const { status, lockDate, startDate } = order;

    if (
      ![Investment_BuyOrderStatus.CREATED, Investment_BuyOrderStatus.ACCEPTED].includes(status) ||
      !lockDate ||
      lockDate === startDate
    ) {
      return null;
    }

    return <Row title={t('Order can be canceled until')}>{lockDate}</Row>;
  }

  private renderRejectedAt() {
    const { t, order } = this.props;

    if (order.isRejected && order.auditInfo.updatedAt) {
      return <Row title={t('Rejected at')}>{order.auditInfo.updatedAt}</Row>;
    }

    return null;
  }

  private renderComment() {
    const { t, order } = this.props;

    if (order.__typename === OrderType.Idea) {
      const { status, rejectComment, product } = order;

      const retryLink =
        product && `/investments/idea/${product.ideaType.toLowerCase()}/${product.id}`;

      switch (status) {
        case Investment_BuyOrderStatus.REJECTED_NO_FUNDS:
          return <Comment>{t('Rejected no funds')}</Comment>;

        case Investment_BuyOrderStatus.CANCELED_BY_ADMIN:
        case Investment_BuyOrderStatus.REJECTED_BY_ADMIN:
          return <Comment retryLink={retryLink}>{rejectComment}</Comment>;

        case Investment_BuyOrderStatus.REJECTED_BY_EXCHANGE:
          if (order.product?.properties.__typename === 'Investment_BuyAndHoldIdeaProperties') {
            const {
              rejectComment,
              product: {
                properties: { manual },
              },
            } = order;

            if (rejectComment && rejectComment.search('KERNEL_NO_LIQUIDITY') > -1) {
              return <Comment>{t('No orders for idea')}</Comment>;
            }

            if (!manual) {
              return <Comment>{t('Couldnt execute your order Please try again later')}</Comment>;
            }
          }

          return <Comment>{t('Error during executing the order on the exchange')}</Comment>;

        case Investment_BuyOrderStatus.REJECTED_AND_ARCHIVED:
          return <Comment>{t(rejectComment)}</Comment>;
      }
    }

    if (order.__typename === OrderType.Stock) {
      switch (order.stockStatus) {
        case Stocks_BuyOrderStatus.REJECTED:
          return <Comment>{t('Error during executing the order on the exchange')}</Comment>;
      }
    }

    return null;
  }
}

export default enhance(StatusTooltip);
